<template>
  <div
    class="max-w-lg mx-auto"
    :class="{ 'load load--fill': form.busy }"
  >
    <form
      method="POST"
      class="mt-8"
      @submit.prevent="save"
    >
      <fieldset class="mb-8">
        <legend
          class="fieldset-legend"
          v-text="'Personal Information:'"
        />
        <div class="field-list">
          <div class="field sm:w-1/2">
            <label
              :for="`${membership.uuid}_first_name`"
              v-text="'First Name'"
            />
            <input
              :id="`${membership.uuid}_first_name`"
              v-model="form.first_name"
              :name="`${membership.uuid}_first_name`"
              type="text"
              placeholder="First Name"
              :class="{ error: form.errors.has('first_name') }"
            >
            <div class="field__meta">
              <span
                v-if="form.errors.has('first_name')"
                class="field__meta__help"
                v-text="form.errors.get('first_name')"
              />
            </div>
          </div>
          <div class="field sm:w-1/2">
            <label
              :for="`${membership.uuid}_last_name`"
              v-text="'Last Name'"
            />
            <input
              :id="`${membership.uuid}_last_name`"
              v-model="form.last_name"
              :name="`${membership.uuid}_last_name`"
              type="text"
              placeholder="Last Name"
              :class="{ error: form.errors.has('last_name') }"
            >
            <div class="field__meta">
              <span
                v-if="form.errors.has('last_name')"
                class="field__meta__help"
                v-text="form.errors.get('last_name')"
              />
              <div class="field__meta__share" />
            </div>
          </div>
          <div class="field sm:w-1/2">
            <label
              :for="`${membership.uuid}_email`"
              v-text="'Contact Email'"
            />
            <input
              :id="`${membership.uuid}_email`"
              v-model="form.email"
              :name="`${membership.uuid}_email`"
              type="email"
              placeholder="Contact Email"
              :class="{ error: form.errors.has('email') }"
            >
            <div class="field__meta">
              <div class="flex">
                <AppCheckboxInput
                  :id="`${membership.uuid}_accepts_email_communication`"
                  v-model="form.accepts_email_communication"
                  :name="`${membership.uuid}_accepts_email_communication`"
                  label="Receives communication?"
                  container-classes="mr-2 field__meta__share"
                />
                <AppCheckboxInput
                  v-if="union.profile_sharing_enabled"
                  :id="`${membership.uuid}_email_is_visible`"
                  v-model="form.email_is_visible"
                  :name="`${membership.uuid}_email_is_visible`"
                  label="Visible?"
                  container-classes="field__meta__share"
                />
              </div>
              <span
                v-if="form.errors.has('email')"
                class="field__meta__help"
                v-text="form.errors.get('email')"
              />
            </div>
          </div>
          <div class="field sm:w-1/2">
            <PhoneNumberFieldComponent
              v-model="form.phone_numbers"
              :errors="form.errors"
              :membership-uuid="membership.uuid"
              :profile-sharing-is-enabled="union.profile_sharing_enabled"
              :default-country="union.country"
            />
          </div>
        </div>
      </fieldset>
      <fieldset class="mb-8">
        <legend
          v-can="'manage-membership-titles'"
          class="fieldset-legend"
          v-text="'Status & Title'"
        />
        <legend
          v-cannot="'manage-membership-titles'"
          class="fieldset-legend"
          v-text="'Status'"
        />
        <div class="field-list">
          <div class="field sm:w-1/2">
            <label
              for="work_status"
              v-text="'Work Status'"
            />
            <WorkStatusFieldComponent
              id="work_status"
              v-model="form.work_status"
            />
          </div>
          <div
            v-if="adminMode"
            class="field sm:w-1/2"
          >
            <label
              for="titles"
              v-text="'Titles'"
            />
            <TitlesFieldComponent
              id="titles"
              v-model="form.titles"
            />
          </div>
        </div>
      </fieldset>
      <fieldset
        v-if="selfAssignableGroupsEnabled"
        class="mb-8"
      >
        <legend
          class="fieldset-legend"
          v-text="'Groups'"
        />
        <div class="field-list">
          <div class="field">
            <label
              for="membership_groups"
              v-text="'Membership Groups'"
            />
            <Multiselect
              id="membership_groups"
              v-model="form.membership_groups"
              :loading="isLoadingGroups"
              :multiple="true"
              :options="availableGroups"
              label="title"
              open-direction="below"
              placeholder="Group(s)"
              track-by="uuid"
              :clear-on-select="false"
              :close-on-select="false"
              @search-change="handleGroupSearchChange"
            />
          </div>
        </div>
      </fieldset>
      <fieldset class="mb-8">
        <legend
          class="fieldset-legend"
          v-text="'Addresses:'"
        />
        <div class="field-list w-full">
          <AddressFieldComponent
            v-model="form.addresses"
            :errors="form.errors"
            :membership-uuid="membership.uuid"
            :profile-sharing-is-enabled="union.profile_sharing_enabled"
            :default-country="union.country"
          />
        </div>
      </fieldset>
      <fieldset class="mb-8">
        <legend
          class="fieldset-legend"
          v-text="'Emergency Contacts:'"
        />
        <EmergencyContactFieldComponent
          v-model="form.emergency_contacts"
          :membership-uuid="membership.uuid"
          :profile-sharing-is-enabled="union.profile_sharing_enabled"
          :addresses="form.addresses"
          :errors="form.errors"
          :default-country="union.country"
        />
      </fieldset>
      <FormBuilderFieldSet
        v-for="element in formBuilderForms"
        :key="`form-${element.form.uuid}`"
        class="mb-8"
        :form="element.form"
        :show-outline="true"
        :submission="element.submission"
        :union-slug="union.slug"
        :flat-picker-date-format="union.flat_picker_date_format"
        :hide-non-member-editable-fields="false"
        @submissionUpdated="updateSubmission"
      />
      <div class="flex justify-around py-4">
        <button
          type="submit"
          class="btn whitespace-normal"
          :class="{ load: form.busy }"
          v-text="saveButtonText"
        />
      </div>
    </form>
  </div>
</template>

<script type="text/babel">
import { debounce } from 'lodash'
import Multiselect from 'vue-multiselect'

import Form from 'Base/Resources/assets/js/plugins/Form'
import AppCheckboxInput from 'Base/Resources/assets/js/components/forms/inputs/AppCheckboxInput'
import FormBuilderFieldSet from 'FormBuilder/Resources/assets/js/components/FormBuilderFieldSet'
import TitlesFieldComponent from 'Membership/Resources/assets/js/components/titles/TitlesFieldComponent'
import WorkStatusFieldComponent from 'Membership/Resources/assets/js/components/work-status/WorkStatusField'
import AddressFieldComponent from 'Membership/Resources/assets/js/components/addresses/AddressFieldComponent'
import PhoneNumberFieldComponent from 'Membership/Resources/assets/js/components/phone-number/PhoneNumberFieldComponent'
import EmergencyContactFieldComponent from 'Membership/Resources/assets/js/components/emergency-contacts/EmergencyContactFieldComponent'

export default {
  name: 'MembershipForm',
  components: {
    Multiselect,
    AppCheckboxInput,
    FormBuilderFieldSet,
    TitlesFieldComponent,
    AddressFieldComponent,
    WorkStatusFieldComponent,
    PhoneNumberFieldComponent,
    EmergencyContactFieldComponent,
  },
  props: {
    membership: {
      type: [Boolean, Object],
      required: false,
      default: false,
    },
    adminMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      form: new Form({
        first_name: '',
        last_name: '',
        email: '',
        email_is_visible: true,
        accepts_email_communication: false,
        work_status: 'active',
        titles: [],
        addresses: [],
        phone_numbers: [],
        emergency_contacts: [],
        membership_groups: [],
        form_builder_fields: [],
      }),
      membership_groups: [],
      formBuilderForms: [],
      hasFormBuilderErrors: false,
      availableGroups: [],
      isLoadingGroups: false,
    }
  },
  computed: {
    saveButtonText() {
      if (this.adminMode) {
        if (this.membership) {
          return this.form.busy ? 'Updating Member...' : 'Update Member'
        }

        return this.form.busy ? 'Creating Member...' : 'Create New Member'
      }

      return this.form.busy ? 'Saving...' : `Save profile for ${this.union.name}`
    },
    union() {
      return this.membership ? this.membership.union.attributes : this.$root.Union
    },
    selfAssignableGroupsEnabled() {
      if (this.adminMode) {
        return false
      }

      return this.union.self_assignable_groups_enabled
    },
  },
  watch: {
    membership() {
      this.fetchMembershipForms()
      this.parseMembershipData(this.membership)
    },
  },
  async mounted() {
    await this.$nextTick()

    if (this.membership) {
      this.fetchMembershipForms()
      this.parseMembershipData(this.membership)

      if (this.selfAssignableGroupsEnabled) {
        this.fetchGroups()
      }
    }
  },
  methods: {
    save() {
      this.form.startProcessing()

      let action = 'post'
      let url = 'memberships'

      if (this.membership) {
        action = 'put'

        url = `memberships/${this.membership.uuid}`

        if (!this.adminMode) {
          url = `me/${url}`
        }
      }

      Promise.all(this.storeFormBuilderFormFields()
        .concat([
          this.adminMode
            ? this.$api.active()[action](url, this.assemblePayload())
            : this.$api.public()[action](url, this.assemblePayload()),
        ]))
        .then((responses) => {
          if (responses[responses.length - 1].data.status === 'failed') {
            this.$store.commit('baseAddNotification', {
              title: 'Error!',
              text: responses[responses.length - 1].data.message,
              type: 'error',
              timeout: false,
            })
          } else {
            this.$store.commit('baseAddNotification', {
              title: 'Success!',
              text: responses[responses.length - 1].data.message,
              type: 'success',
            })

            if (this.membership) {
              this.$emit('membershipHasBeenUpdated')
            } else {
              this.$emit('setMembershipUuid', responses[responses.length - 1].data.meta.membershipUuid)
            }
          }

          this.fetchMembershipForms()
          this.form.finishProcessing()
        })
        .catch(({ response }) => {
          this.form.setErrors(response.data)

          this.formBuilderForms.forEach((form) => {
            form.submission.setErrors(response.data)
          })

          this.form.finishProcessing()
        })
    },
    parseMembershipData(membershipData) {
      this.form.first_name = membershipData.attributes.first_name
      this.form.last_name = membershipData.attributes.last_name
      this.form.email = membershipData.attributes.email
      this.form.email_is_visible = membershipData.attributes.email_is_visible
      this.form.accepts_email_communication = membershipData.attributes.accepts_email_communication
      this.form.work_status = membershipData.attributes.work_status

      if (this.adminMode) {
        this.form.titles = _.map(membershipData.membershipTitles.data, (title) => {
          return {
            uuid: title.uuid,
            label: title.attributes.label,
          }
        })
      }

      this.form.phone_numbers = membershipData.phoneNumbers.data.map(phoneNumber => ({
        uuid: phoneNumber.uuid,
        is_visible: phoneNumber.attributes.is_visible,
        receives_sms: phoneNumber.attributes.textable,
        phone_number: phoneNumber.attributes.phone_number,
        phone_number_country: phoneNumber.attributes.phone_number_country,
      }))

      this.form.addresses = membershipData.addresses.data.map(address => ({
        uuid: address.uuid,
        address_line_1: address.attributes.address_line_1,
        address_line_2: address.attributes.address_line_2,
        city: address.attributes.city,
        state: address.attributes.region,
        zip: address.attributes.postal_code,
        type_id: address.attributes.type_id,
        is_visible: address.attributes.is_visible,
        country: address.attributes.country,
      }))

      this.form.emergency_contacts = membershipData.emergencyContacts.data.map(emergencyContact => ({
        uuid: emergencyContact.uuid,
        first_name: emergencyContact.attributes.first_name,
        last_name: emergencyContact.attributes.last_name,
        phone_number: emergencyContact.attributes.phone_number,
        phone_number_country: emergencyContact.attributes.phone_number_country,
        relationship: emergencyContact.attributes.relationship,
        is_visible: emergencyContact.attributes.is_visible,
      }))

      if (!this.adminMode) {
        this.membership_groups = membershipData.selfAssignableGroups.data.map(group => ({
          title: group.attributes.title,
          uuid: group.uuid,
        }))
      }
    },
    fetchMembershipForms() {
      const vm = this

      this.$api.public().get(`unions/${this.union.slug}/forms`, {
        params: {
          scope: 'membership',
          include: 'hiddenFields',
        },
      }).then((response) => {
        const membershipForms = response.data.data

        vm.formBuilderForms = membershipForms.map(function (form) {
          const data = { membership_id: vm.membership ? vm.membership.uuid : null }

          form.fields.data.forEach(function (field) {
            data[field.attributes.slug] = vm.membership.attributes[field.attributes.slug]
              ? vm.membership.attributes[field.attributes.slug]
              : null
          })

          return {
            form: form,
            submission: new Form(data),
          }
        })
      })
    },
    updateSubmission(event) {
      const found = this.formBuilderForms.find(function (element) {
        return element.form.uuid === event.form_id
      })

      if (found) {
        found.submission = event.submission
      }
    },
    storeFormBuilderFormFields() {
      const vm = this

      return this.formBuilderForms.map(function (element) {
        return vm.$api.public().post(`unions/${vm.union.slug}/forms/${element.form.uuid}/submit`, element.submission)
      })
    },
    assemblePayload() {
      const payload = _.cloneDeep(this.form)

      if (!this.adminMode) {
        delete payload.titles
      }

      this.formBuilderForms.forEach(function (element) {
        payload.form_builder_fields.push(element.submission)
      })

      payload.phone_numbers = payload.phone_numbers.filter((phoneNumber) => {
        return phoneNumber.phone_number
      })

      payload.emergency_contacts = payload.emergency_contacts.filter((emergency_contact) => {
        return emergency_contact.last_name
      })

      return payload
    },
    handleGroupSearchChange: debounce(function (search = '') {
      this.fetchGroups(search)
    }, 500),
    fetchGroups(search = '') {
      this.isLoadingGroups = true

      this.$api.public()
        .get(`unions/${this.union.slug}/membership-groups?per_page=100&query=${search}`)
        .then((response) => {
          this.availableGroups = response.data.data.map((group) => {
            return {
              title: group.attributes.title,
              uuid: group.uuid,
            }
          })

          this.isLoadingGroups = false
        })
    },
  },
}
</script>
